import { z } from 'zod'

import type { ContributorDetails, StrainDraft } from '../types/submission'

const MAX_FILE_BYTES = 25 * 1024 * 1024
const MAX_IMAGES_PER_STRAIN = 20
const MAX_STRAINS = 10
const ACCEPTED_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
])

export const detailsSchema = z.object({
  contactName: z.string().trim().min(2, 'Enter your name.'),
  contactEmail: z.email('Enter a valid email address.'),
  creditName: z.string().trim().min(2, 'Enter the name High Times should credit.'),
  socialLinks: z.object({
    instagram: z.string(),
    facebook: z.string(),
    x: z.string(),
    website: z.string(),
  }),
})

export interface ValidationResult {
  valid: boolean
  message?: string
}

export function validateDetails(details: ContributorDetails): ValidationResult {
  const result = detailsSchema.safeParse(details)
  if (result.success) return { valid: true }
  return { valid: false, message: result.error.issues[0]?.message ?? 'Check your details.' }
}

export function validateFile(file: File): ValidationResult {
  if (!ACCEPTED_IMAGE_TYPES.has(file.type.toLowerCase())) {
    return { valid: false, message: `${file.name} is not a supported image format.` }
  }
  if (file.size > MAX_FILE_BYTES) {
    return { valid: false, message: `${file.name} is larger than 25 MB.` }
  }
  if (file.size === 0) {
    return { valid: false, message: `${file.name} is empty.` }
  }
  return { valid: true }
}

export function validateStrains(strains: StrainDraft[]): ValidationResult {
  if (strains.length === 0) return { valid: false, message: 'Add at least one strain.' }
  if (strains.length > MAX_STRAINS) {
    return { valid: false, message: `A submission can include up to ${MAX_STRAINS} strains.` }
  }

  for (const [index, strain] of strains.entries()) {
    if (strain.name.trim().length < 2) {
      return { valid: false, message: `Enter a name for strain ${index + 1}.` }
    }
    if (strain.assets.length === 0) {
      return { valid: false, message: `Add at least one image for ${strain.name || `strain ${index + 1}`}.` }
    }
    if (strain.assets.length > MAX_IMAGES_PER_STRAIN) {
      return {
        valid: false,
        message: `${strain.name} has more than ${MAX_IMAGES_PER_STRAIN} images.`,
      }
    }
    const incompleteAsset = strain.assets.find((asset) => asset.status !== 'ready')
    if (incompleteAsset) {
      return {
        valid: false,
        message:
          incompleteAsset.status === 'error'
            ? `Remove or retry ${incompleteAsset.name}.`
            : `Wait for ${incompleteAsset.name} to finish processing.`,
      }
    }
  }
  return { valid: true }
}

export const uploadLimits = {
  acceptedTypes: [...ACCEPTED_IMAGE_TYPES],
  maxFileBytes: MAX_FILE_BYTES,
  maxImagesPerStrain: MAX_IMAGES_PER_STRAIN,
  maxStrains: MAX_STRAINS,
}
